import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useOutletContext } from "react-router-dom";
import toast from "react-hot-toast";
import { CheckCircle2 } from "lucide-react";

import { CenteredLoader } from "@/components/shared/Loader";
import AlertModal from "@/components/shared/AlertModal";

import BrandingApi from "./api";
import { BRANDING_FORM_DEFAULTS } from "./constants";
import { mapBrandingState, mapCurrentToFormValues } from "./mapping";
import brandingValidationSchema from "./validation.schema";

import BrandingForm from "./components/BrandingForm";
import BrandingAssetUploads from "./components/BrandingAssetUploads";
import BrandingPublishedPanel from "./components/BrandingPublishedPanel";
import BrandingHistoryPanel from "./components/BrandingHistoryPanel";
import BrandingActionBar from "./components/BrandingActionBar";
import BrandingPreview from "./components/BrandingPreview";

import ModuleUnavailableNotice from "@/permissions/ModuleUnavailableNotice";
import useModuleAccess from "@/hooks/useModuleAccess";
import { MODULES } from "@/permissions/modules";

import {
  BRANDING_SHELL,
  BRANDING_SHELL_STYLE,
  BRANDING_ROW_PADDING,
  BRANDING_STACK_GAP,
} from "./branding.tokens";

// Color configuration is disabled — see BrandingForm.jsx / BrandingProvider.jsx.
// import { BRANDING_COLOR_DEFAULTS } from "./constants";
// const ensureHash = (v) => (v && !v.startsWith("#") ? `#${v}` : v || "");
// const BRANDING_COLOR_FIELDS = ["primaryColor", "primaryColorContrast", "primaryColorDark", "primaryColorContrastDark"];

const DEFAULTS = {
  displayName: "IMS People Possible AI",
  // ...BRANDING_COLOR_DEFAULTS,
};

const ASSET_PUBLISH_LABELS = {
  logo: "Logo",
  logo_dark: "Dark logo",
  favicon: "Favicon",
  email_logo: "Email logo",
};

const CONFIRM_ACTIONS = {
  PUBLISH: "publish",
  DISCARD: "discard",
  DISCARD_CONFIG: "discard_config",
  RESET: "reset",
  REMOVE_DRAFT_ASSET: "remove_draft_asset",
  REMOVE_PUBLISHED_ASSET: "remove_published_asset",
  RESTORE_HISTORY: "restore_history",
};

const BrandingPage = () => {
  const setHeader = useOutletContext();

  const methods = useForm({
    defaultValues: BRANDING_FORM_DEFAULTS,
    resolver: yupResolver(brandingValidationSchema),
    mode: "onChange",
  });

  const {
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting },
  } = methods;

  const watchedValues = useWatch({ control: methods.control });

  // The fleet-wide kill switch. Entitlement is the route's job — a tenant
  // without `branding` never reaches this page. Availability is this page's job,
  // and it means exactly one thing here: every write is refused with 503 while
  // reads stay open, so every control that writes is disabled and nothing else
  // changes.
  const { isAvailable } = useModuleAccess(MODULES.BRANDING);
  const writesBlocked = !isAvailable;

  const [loading, setLoading] = useState(true);
  const [pageRefreshing, setPageRefreshing] = useState(false);

  const [brandingState, setBrandingState] = useState({
    published: null,
    draft: null,
    publishedAssets: {},
    draftAssets: {},
    quotaBytes: 0,
    quotaUsedBytes: 0,
  });

  const [history, setHistory] = useState([]);
  const [historyPagination, setHistoryPagination] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(10);
  const [historyLoading, setHistoryLoading] = useState(false);
  // What the form was loaded with (draft config if one exists, else published).
  // Save Draft only means something when a branding field actually moved away
  // from this baseline — saving an untouched form used to mint a new draft,
  // and publishing that draft bumped the version number with zero changes.
  const [baseline, setBaseline] = useState({ displayName: "" });
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [isDiscardingConfig, setIsDiscardingConfig] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [assetLoadingKey, setAssetLoadingKey] = useState(null);
  const [confirmState, setConfirmState] = useState({
    open: false,
    action: null,
    payload: null,
  });

  // A short-lived "Saved just now" / "Published" readout so a successful
  // save or publish is visible even though nothing else on the page changes
  // at that instant. Cleared early if the user starts editing again — a
  // stale "Saved" while the form is dirty would be worse than none.
  const [transientStatus, setTransientStatus] = useState(null);
  const transientTimeoutRef = useRef(null);
  const flashStatus = useCallback((label, tone, ms = 3000) => {
    if (transientTimeoutRef.current) clearTimeout(transientTimeoutRef.current);
    setTransientStatus({ label, tone });
    transientTimeoutRef.current = setTimeout(() => setTransientStatus(null), ms);
  }, []);
  useEffect(() => () => {
    if (transientTimeoutRef.current) clearTimeout(transientTimeoutRef.current);
  }, []);

  useEffect(() => {
    setHeader?.({
      title: "Tenant Branding",
      subTitle:
        "Manage draft branding, live assets, published versions, and rollback history.",
    });
    return () => setHeader?.({ title: "", subTitle: "" });
  }, [setHeader]);

  const loadPageData = useCallback(async ({ silent = false } = {}) => {
    try {
      silent ? setPageRefreshing(true) : setLoading(true);
      const { current, history: historyRes } = await BrandingApi.loadPageData();
      setBrandingState(mapBrandingState(current));
      // A full reload (mount, or after a publish/discard/rollback) always
      // lands back on history page 1 — a publish/rollback adds a new top
      // row, so page 1 is also where the change the user just made shows up.
      setHistory(historyRes?.items ?? []);
      setHistoryPagination(historyRes?.pagination ?? null);
      setHistoryPage(1);
      const formValues = mapCurrentToFormValues(current);
      reset(formValues);
      setBaseline({
        displayName: formValues.displayName || "",
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load branding data");
    } finally {
      setLoading(false);
      setPageRefreshing(false);
    }
  }, [reset]);

  // Turning the page (or changing page size) only needs a fresh history
  // list — it never touches `current`/draft state, so it doesn't go through
  // loadPageData and doesn't disturb any in-progress form edit.
  const fetchHistoryPage = useCallback(async (page, pageSize) => {
    try {
      setHistoryLoading(true);
      const historyRes = await BrandingApi.getHistoryPage({ page, perPage: pageSize });
      setHistory(historyRes?.items ?? []);
      setHistoryPagination(historyRes?.pagination ?? null);
      setHistoryPage(page);
      setHistoryPageSize(pageSize);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load branding history");
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => { loadPageData(); }, [loadPageData]);

  // Refreshes only asset/quota state (+ the draft concurrency stamp) without
  // touching the text-field form — used after asset-only mutations (upload,
  // remove draft/published asset) so unsaved displayName edits the user
  // hasn't clicked "Save Draft" on yet aren't wiped out by a full form
  // reset().
  const refreshBrandingAssets = useCallback(async () => {
    const current = await BrandingApi.getCurrent();
    setBrandingState(mapBrandingState(current));
    setValue("expectedUpdatedAt", current?.draft?.updatedAt || null, {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [setValue]);

  const quotaUsageText = useMemo(() => {
    const used = brandingState?.quotaUsedBytes || 0;
    const total = brandingState?.quotaBytes || 0;
    if (!total) return "0 MB / 0 MB";
    const toMb = (v) => (v / (1024 * 1024)).toFixed(2);
    return `${toMb(used)} MB / ${toMb(total)} MB`;
  }, [brandingState?.quotaBytes, brandingState?.quotaUsedBytes]);

  const handleSaveDraft = handleSubmit(async (values) => {
    try {
      const payload = {
        displayName: values.displayName?.trim() || "",
        // primaryColor: ensureHash(values.primaryColor),
        // primaryColorContrast: ensureHash(values.primaryColorContrast),
        // primaryColorDark: ensureHash(values.primaryColorDark),
        // primaryColorContrastDark: ensureHash(values.primaryColorContrastDark),
        expectedUpdatedAt: values.expectedUpdatedAt || null,
      };
      const draftRecord = await BrandingApi.saveDraft(payload);
      toast.success("Draft saved successfully");
      flashStatus("Saved just now", "emerald");
      setBrandingState((prev) => ({
        ...prev,
        draft: draftRecord,
        draftAssets: (draftRecord?.assets || []).reduce((acc, a) => {
          acc[a.assetKind] = a;
          return acc;
        }, {}),
      }));
      setValue("expectedUpdatedAt", draftRecord?.updatedAt || null, {
        shouldDirty: false,
        shouldValidate: false,
      });
      await loadPageData({ silent: true });
    } catch (error) {
      const code = error?.response?.data?.error_code;
      const message = error?.response?.data?.message || "Failed to save draft";
      if (code === "BRANDING_STALE_DRAFT") {
        toast.error("Draft was updated elsewhere. Reloading latest version.");
        await loadPageData({ silent: true });
        return;
      }
      // Color configuration is disabled — kept for a possible future
      // re-enable alongside the contrast-check UI in ColorPicker.jsx.
      // if (code === "BRANDING_CONTRAST_INSUFFICIENT") {
      //   const failures = error?.response?.data?.data?.failures || [];
      //   failures.forEach((failure) => {
      //     if (!failure?.field) return;
      //     setError(failure.field, {
      //       type: "manual",
      //       message: `Contrast ratio ${failure.ratio}:1 is below the required ${failure.required}:1 (WCAG AA).`,
      //     });
      //   });
      //   toast.error(message);
      //   return;
      // }
      toast.error(message);
    }
  });

  // Reverts displayName back to what it was last loaded with — purely
  // local, no API call. Only meaningful before a Save Draft: once saved,
  // the form already matches this baseline, so there's nothing left to
  // revert here — see CONFIRM_ACTIONS.DISCARD_CONFIG for that case.
  const handleDiscardChanges = () => {
    setValue("displayName", baseline.displayName, { shouldDirty: false, shouldValidate: false });
  };

  const openConfirm = (action, payload = null) => setConfirmState({ open: true, action, payload });
  const closeConfirm = () => setConfirmState({ open: false, action: null, payload: null });

  const handleUploadAsset = async (assetKind, file) => {
    try {
      setAssetLoadingKey(`upload-${assetKind}`);
      await BrandingApi.uploadAsset({ assetKind, file });
      toast.success("Asset uploaded successfully");
      await refreshBrandingAssets();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to upload asset");
    } finally {
      setAssetLoadingKey(null);
    }
  };

  const dispatchBrandingPublishedEvent = () => {
    window.dispatchEvent(new CustomEvent("branding-published"));
  };

  const handleConfirmAction = async () => {
    const { action, payload } = confirmState;
    if (isPublishing || isDiscarding || isDiscardingConfig || isResetting || assetLoadingKey) return;
    try {
      if (action === CONFIRM_ACTIONS.PUBLISH) {
        setIsPublishing(true);
        await BrandingApi.publishDraft();
        toast.success("Branding published successfully");
        flashStatus("Published", "emerald");
        dispatchBrandingPublishedEvent();
      }
      if (action === CONFIRM_ACTIONS.DISCARD) {
        setIsDiscarding(true);
        await BrandingApi.discardDraft();
        toast.success("Draft discarded successfully");
      }
      if (action === CONFIRM_ACTIONS.DISCARD_CONFIG) {
        setIsDiscardingConfig(true);
        await BrandingApi.discardDraftConfig();
        toast.success("Draft text reverted to published");
      }
      if (action === CONFIRM_ACTIONS.REMOVE_DRAFT_ASSET) {
        setAssetLoadingKey(`remove-draft-${payload?.assetKind}`);
        await BrandingApi.deleteAsset({ assetKind: payload?.assetKind, scope: "draft" });
        toast.success("Draft asset removed");
        closeConfirm();
        await refreshBrandingAssets();
        return;
      }
      if (action === CONFIRM_ACTIONS.REMOVE_PUBLISHED_ASSET) {
        setAssetLoadingKey(`remove-published-${payload?.assetKind}`);
        await BrandingApi.deleteAsset({ assetKind: payload?.assetKind, scope: "published" });
        toast.success("Live asset removed");
        dispatchBrandingPublishedEvent();
        closeConfirm();
        await refreshBrandingAssets();
        return;
      }
      if (action === CONFIRM_ACTIONS.RESET) {
        setIsResetting(true);
        reset(DEFAULTS);
        const assetKinds = ["logo", "logo_dark", "favicon", "email_logo"];
        await Promise.allSettled([
          ...assetKinds.map((kind) =>
            BrandingApi.deleteAsset({ assetKind: kind, scope: "published" }).catch(() => {})
          ),
          ...assetKinds.map((kind) =>
            BrandingApi.deleteAsset({ assetKind: kind, scope: "draft" }).catch(() => {})
          ),
        ]);
        await BrandingApi.discardDraft().catch(() => {});
        const defaultPayload = {
          displayName: DEFAULTS.displayName,
          // primaryColor: DEFAULTS.primaryColor,
          // primaryColorContrast: DEFAULTS.primaryColorContrast,
          // primaryColorDark: DEFAULTS.primaryColorDark,
          // primaryColorContrastDark: DEFAULTS.primaryColorContrastDark,
          expectedUpdatedAt: null,
        };
        await BrandingApi.saveDraft(defaultPayload);
        await BrandingApi.publishDraft();
        toast.success("Branding reset to defaults");
        dispatchBrandingPublishedEvent();
        // Falls through to the shared closeConfirm()/loadPageData() below —
        // the confirm modal (and its loading-disabled Cancel/Confirm buttons)
        // must stay up for the whole reset, same as Publish/Discard, so the
        // rest of the page can't be interacted with while this is in flight.
      }
      if (action === CONFIRM_ACTIONS.RESTORE_HISTORY) {
        setAssetLoadingKey(`restore-${payload?.id}`);
        await BrandingApi.rollbackToHistory(payload?.id);
        toast.success("Branding restored successfully");
        dispatchBrandingPublishedEvent();
      }
      closeConfirm();
      await loadPageData({ silent: true });
    } catch (error) {
      toast.error(error?.response?.data?.message || "Action failed");
    } finally {
      setIsPublishing(false);
      setIsDiscarding(false);
      setIsDiscardingConfig(false);
      setIsResetting(false);
      setAssetLoadingKey(null);
    }
  };

  // What Publish would actually change, and the version it lands on — shown
  // in the publish confirmation so "make this live" isn't a leap of faith.
  // Declared ahead of confirmTitle/confirmDescription below: useMemo's
  // factory runs synchronously at render time, so those memos would read
  // this before its declaration (a TDZ error) if it came after them.
  const publishChanges = useMemo(() => {
    if (!brandingState?.draft) return [];
    const draftConfig = brandingState.draft.config || {};
    const publishedConfig = brandingState.published?.config || {};
    const changes = [];
    if ((draftConfig.displayName ?? "") !== (publishedConfig.displayName ?? "")) changes.push("Display name");
    Object.entries(ASSET_PUBLISH_LABELS).forEach(([kind, label]) => {
      const draftUrl = brandingState.draftAssets?.[kind]?.url || null;
      const publishedUrl = brandingState.publishedAssets?.[kind]?.url || null;
      if (draftUrl !== publishedUrl) changes.push(label);
    });
    return changes;
  }, [brandingState]);
  const nextPublishVersion = (brandingState?.published?.version || 0) + 1;

  // What Restore would actually change, mirroring publishChanges above.
  // Rollback only ever touches config (see service.py's rollback_to — "v1
  // rollback restores the config only"), never assets, no matter what
  // asset_kinds the historical row lists — so this only ever compares
  // display name, and the confirmation says as much about assets.
  const restoreChanges = useMemo(() => {
    if (confirmState.action !== CONFIRM_ACTIONS.RESTORE_HISTORY || !confirmState.payload) return [];
    const target = confirmState.payload;
    const publishedConfig = brandingState.published?.config || {};
    const changes = [];
    if ((target.displayName ?? "") !== (publishedConfig.displayName ?? "")) {
      changes.push({ label: "Display name", value: target.displayName || "—" });
    }
    return changes;
  }, [confirmState.action, confirmState.payload, brandingState]);

  const confirmTitle = useMemo(() => {
    switch (confirmState.action) {
      case CONFIRM_ACTIONS.PUBLISH: return "Publish Branding";
      case CONFIRM_ACTIONS.DISCARD: return "Discard All Draft";
      case CONFIRM_ACTIONS.DISCARD_CONFIG: return "Discard Draft Text";
      case CONFIRM_ACTIONS.RESET: return "Reset to IMS Defaults";
      case CONFIRM_ACTIONS.REMOVE_DRAFT_ASSET: return "Remove Draft Asset";
      case CONFIRM_ACTIONS.REMOVE_PUBLISHED_ASSET: return "Remove Live Asset";
      case CONFIRM_ACTIONS.RESTORE_HISTORY: return "Restore Branding Version";
      default: return "Confirm Action";
    }
  }, [confirmState.action]);

  const confirmDescription = useMemo(() => {
    switch (confirmState.action) {
      case CONFIRM_ACTIONS.PUBLISH:
        // AlertModal's `description` renders inside Radix's <Description>,
        // which is a <p> — only inline-content-model tags (span, br, svg) are
        // valid inside it. A <div>/<ul>/<li> here would be invalid nesting
        // that the browser silently un-nests, breaking the layout.
        return (
          <span className="flex flex-col gap-2.5">
            <span className="block">You're about to make these changes live for tenant-facing branding surfaces:</span>
            {publishChanges.length > 0 ? (
              <span className="flex flex-col gap-1">
                {publishChanges.map((label) => (
                  <span key={label} className="flex items-center gap-1.5 text-text-primary">
                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden="true" />
                    {label}
                  </span>
                ))}
              </span>
            ) : (
              <span className="block text-text-secondary">No field changes — this republishes the current draft as-is.</span>
            )}
            <span className="block text-text-secondary">
              Version <span className="font-medium text-text-primary">v{nextPublishVersion}</span>
            </span>
          </span>
        );
      case CONFIRM_ACTIONS.DISCARD: return "This will permanently discard the current draft and uploaded draft assets.";
      case CONFIRM_ACTIONS.DISCARD_CONFIG: return "This reverts the saved draft's display name back to what's currently published. Draft assets (logo, favicon, etc.) are not affected.";
      case CONFIRM_ACTIONS.RESET: return "This immediately clears and publishes the default IMS People Possible AI branding — logo and display name — live for all users. There is no separate review step; this cannot be undone from here.";
      case CONFIRM_ACTIONS.REMOVE_DRAFT_ASSET: return "This removes the asset from the draft only.";
      case CONFIRM_ACTIONS.REMOVE_PUBLISHED_ASSET: return "This removes the live asset immediately.";
      case CONFIRM_ACTIONS.RESTORE_HISTORY:
        return (
          <span className="flex flex-col gap-2.5">
            <span className="block">
              This immediately publishes v{confirmState.payload?.version}'s branding live, overriding what's currently published — there is no separate review step, and this cannot be undone from here.
            </span>
            {restoreChanges.length > 0 ? (
              <span className="flex flex-col gap-1">
                <span className="block text-text-secondary">This changes:</span>
                {restoreChanges.map((change) => (
                  <span key={change.label} className="flex items-center gap-1.5 text-text-primary">
                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden="true" />
                    {change.label}: <span className="font-medium">{change.value}</span>
                  </span>
                ))}
              </span>
            ) : (
              <span className="block text-text-secondary">No display name change — this republishes v{confirmState.payload?.version} as the new current version.</span>
            )}
            <span className="block text-text-secondary">
              Assets (logo, favicon, etc.) are not affected by restoring — only the display name changes. Your current draft is untouched.
            </span>
            <span className="block text-text-secondary">
              Version <span className="font-medium text-text-primary">v{nextPublishVersion}</span>
            </span>
          </span>
        );
      default: return "Please confirm this action.";
    }
  }, [confirmState.action, confirmState.payload, publishChanges, restoreChanges, nextPublishVersion]);

  const confirmActionText = confirmState.action === CONFIRM_ACTIONS.PUBLISH ? `Publish v${nextPublishVersion}` : "Confirm";

  const hasDraft = Boolean(brandingState?.draft);

  // Whether the SAVED draft's text actually differs from what's published —
  // not just whether a draft row exists. discardDraftConfig() reverts the
  // draft's text to published without deleting the row, so right after
  // using it `hasDraft` stays true (the row is still there) even though
  // there's nothing left to discard. This is the gate the Draft Branding
  // card's own "Discard Draft" uses instead of raw `hasDraft`, so it
  // disappears the moment it would have nothing to do.
  const hasDraftTextDiff = useMemo(() => {
    if (!brandingState?.draft) return false;
    const draftConfig = brandingState.draft.config || {};
    const publishedConfig = brandingState.published?.config || {};
    return (draftConfig.displayName ?? "") !== (publishedConfig.displayName ?? "");
  }, [brandingState]);

  // A branding field moved away from what the page loaded with. Drives the
  // Save Draft gate — an untouched form has nothing to save.
  const hasUnsavedChanges = useMemo(() => {
    const displayName = (watchedValues?.displayName ?? "").trim();
    return displayName !== baseline.displayName.trim();
  }, [watchedValues?.displayName, baseline]);

  // Publish is only meaningful when the draft would actually change something:
  // its config differs from what's live, or it carries draft-only assets to
  // promote. Publishing a byte-identical draft is what inflated the version
  // counter without any real change.
  const canPublish = useMemo(() => {
    if (!brandingState?.draft) return false;
    if (!brandingState?.published) return true;
    if (Object.keys(brandingState?.draftAssets || {}).length > 0) return true;
    return (
      (brandingState.draft.config?.displayName ?? "") !==
      (brandingState.published.config?.displayName ?? "")
    );
  }, [brandingState]);

  // Reset to IMS Defaults is hidden once there's nothing left it would
  // change: no custom logo/dark logo/favicon/email logo anywhere, and
  // whatever config exists (draft, published, or both) already reads as the
  // stock IMS name.
  const showReset = useMemo(() => {
    const configMatchesDefaults = (config) =>
      !config || (config.displayName ?? "") === DEFAULTS.displayName;

    const hasAnyAsset =
      Object.keys(brandingState?.draftAssets || {}).length > 0 ||
      Object.keys(brandingState?.publishedAssets || {}).length > 0;

    return (
      hasAnyAsset ||
      !configMatchesDefaults(brandingState?.published?.config) ||
      !configMatchesDefaults(brandingState?.draft?.config)
    );
  }, [brandingState]);

  // Once the user edits again, a leftover "Saved just now" / "Published"
  // flash would read as a lie — clear it the moment the form goes dirty.
  useEffect(() => {
    if (hasUnsavedChanges && transientTimeoutRef.current) {
      clearTimeout(transientTimeoutRef.current);
      setTransientStatus(null);
    }
  }, [hasUnsavedChanges]);

  // A single at-a-glance read of where the tenant's branding stands, next to
  // the actions that change it — without it, telling "live" apart from
  // "draft has changes nobody published yet" meant reading every panel below.
  // In-flight and just-finished states take priority over the steady-state
  // read, so the user never has to wonder whether their click landed.
  const statusBadge = useMemo(() => {
    if (isSubmitting) return { label: "Saving...", tone: "amber" };
    if (isPublishing) return { label: "Publishing...", tone: "amber" };
    if (transientStatus) return transientStatus;
    if (hasUnsavedChanges) return { label: "Unsaved changes", tone: "amber" };
    if (hasDraft && canPublish) return { label: "Draft ready to publish", tone: "amber" };
    return { label: "Saved", tone: "emerald" };
  }, [isSubmitting, isPublishing, transientStatus, hasUnsavedChanges, hasDraft, canPublish]);

  if (loading) return <CenteredLoader />;

  return (
    <>
      <FormProvider {...methods}>
        <div className={BRANDING_STACK_GAP}>
          {/* Killed fleet-wide: the page still loads and the published branding
              still reads, because the route gate tests entitlement only and a
              kill never closes a read. Every write control below is disabled
              from `writesBlocked`, and this is the one line that says why. The
              page used to render fully live and answer 503 on the first click. */}
          <ModuleUnavailableNotice module={MODULES.BRANDING} suffix="The published branding keeps serving, and you can still read every version below." />

          <div className={`${BRANDING_SHELL} ${BRANDING_ROW_PADDING}`} style={BRANDING_SHELL_STYLE}>
            <div className="flex flex-col gap-1.5 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-xs font-medium text-text-primary">Edit → Preview → Save → Publish</p>
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                      statusBadge.tone === "amber" ? "bg-amber-500/10 text-amber-600" : "bg-emerald-500/10 text-emerald-600"
                    }`}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${statusBadge.tone === "amber" ? "bg-amber-500" : "bg-emerald-500"}`}
                      aria-hidden="true"
                    />
                    {statusBadge.label}
                  </span>
                </div>
                <p className="text-xs text-text-secondary">
                  Update your tenant identity and assets, then preview before publishing.
                </p>
              </div>
              <BrandingActionBar
                disabled={pageRefreshing || isSubmitting || writesBlocked}
                hasDraft={hasDraft}
                canPublish={canPublish}
                showReset={showReset}
                isPublishing={isPublishing}
                isDiscarding={isDiscarding}
                isResetting={isResetting}
                onPublish={() => openConfirm(CONFIRM_ACTIONS.PUBLISH)}
                onDiscard={() => openConfirm(CONFIRM_ACTIONS.DISCARD)}
                onReset={() => openConfirm(CONFIRM_ACTIONS.RESET)}
              />
            </div>
          </div>

          {pageRefreshing && (
            <div className={`${BRANDING_SHELL} ${BRANDING_ROW_PADDING}`} style={BRANDING_SHELL_STYLE}>
              <div className="flex items-center justify-between gap-2 text-xs">
                {/* Storage usage temporarily hidden — quotaUsedBytes is not
                    reflecting real asset sizes correctly, needs investigation
                    before showing this to users again.
                <div className="text-text-secondary">
                  Storage usage: <span className="font-medium text-text-primary">{quotaUsageText}</span>
                </div>
                */}
                <div className="text-text-secondary">Refreshing...</div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
            {/* Published Branding sits with the form, not under the preview
                — the form alone (two fields) is far shorter than the
                preview hero, and stacking both under the preview left the
                form's column mostly empty air beside it. */}
            <div className="space-y-3 xl:col-span-7">
              <BrandingForm
                disabled={pageRefreshing || isSubmitting || writesBlocked}
                canSave={hasUnsavedChanges}
                hasDraftTextChange={hasDraftTextDiff}
                isSaving={isSubmitting}
                isDiscarding={isDiscardingConfig}
                onSave={handleSaveDraft}
                onDiscardChanges={handleDiscardChanges}
                onDiscardDraft={() => openConfirm(CONFIRM_ACTIONS.DISCARD_CONFIG)}
              />
              <BrandingPublishedPanel
                published={brandingState?.published}
                publishedAssets={brandingState?.publishedAssets}
              />
            </div>
            <div className="space-y-3 xl:col-span-5">
              <BrandingPreview
                values={watchedValues}
                draftAssets={brandingState?.draftAssets}
                publishedAssets={brandingState?.publishedAssets}
              />
            </div>
          </div>

          <BrandingAssetUploads
            draftAssets={brandingState?.draftAssets}
            publishedAssets={brandingState?.publishedAssets}
            disabled={pageRefreshing || writesBlocked}
            assetLoadingKey={assetLoadingKey}
            onUpload={handleUploadAsset}
            onRemoveDraft={(assetKind) => openConfirm(CONFIRM_ACTIONS.REMOVE_DRAFT_ASSET, { assetKind })}
            onRemovePublished={(assetKind) => openConfirm(CONFIRM_ACTIONS.REMOVE_PUBLISHED_ASSET, { assetKind })}
          />

          <BrandingHistoryPanel
            history={history}
            currentVersion={brandingState?.published?.version}
            disabled={pageRefreshing || writesBlocked}
            loading={historyLoading}
            page={historyPage}
            pageSize={historyPageSize}
            total={historyPagination?.total ?? history.length}
            onPageChange={(page) => !historyLoading && fetchHistoryPage(page, historyPageSize)}
            onPageSizeChange={(pageSize) => !historyLoading && fetchHistoryPage(1, pageSize)}
            onRestore={(item) => openConfirm(CONFIRM_ACTIONS.RESTORE_HISTORY, item)}
          />
        </div>
      </FormProvider>

      <AlertModal
        open={confirmState.open}
        onOpenChange={(o) => { if (!o) closeConfirm(); }}
        onAction={handleConfirmAction}
        onCancel={closeConfirm}
        title={confirmTitle}
        description={confirmDescription}
        showCloseButton
        closeOnOverlayClick
        // AlertDialogContent's own width comes from `data-[size=default]:max-w-xs`
        // / `data-[size=default]:sm:max-w-sm` — both more specific than a plain
        // `max-w-lg` override, so it takes the `!` important modifier to win
        // without touching AlertModal.jsx or AlertDialog.jsx.
        contentClassName="!max-w-lg"
        actionText={confirmActionText}
        actionClassName="bg-button-secondary text-text-base"
        cancelText="Cancel"
        loading={isPublishing || isDiscarding || isDiscardingConfig || isResetting || Boolean(assetLoadingKey)}
      />
    </>
  );
};

export default BrandingPage;